import type { Dayjs } from '@atlas/tool/box/date/dayjs';
import dayjs from '@atlas/tool/box/date/dayjs';

export const isCurrentYear = (date: Dayjs) => date.isSame(dayjs(), 'year');

export const formatWithAlternativeYear = (date: Dayjs, formatWithoutYear: string, formatWithYear: string) => {
    const isThisYear = isCurrentYear(date);

    return date.format(isThisYear ? formatWithoutYear : formatWithYear);
};

export const parseDateUTC = (iso: string): Dayjs => {
    // "Z" indicates UTC time, otherwise either dayjs(...) or new Date(...) parse it as local time
    return dayjs(typeof iso === 'string' && iso.endsWith('Z') ? iso : iso + 'Z');
};

export const parseDate = (iso: string): Dayjs => {
    return parseDateUTC(iso).tz();
};

export const shortTextDate = function (date: Dayjs) {
    if (!date) {
        return '';
    }
    return date.format('ll');
};

export const shortDateTimeDisplay = function (
    date: Dayjs | string,
    includeWeekday = false,
    separator = ', ',
    shortWeekday = false,
) {
    if (!date) {
        return '';
    }

    date = dayjs(date);

    if (dayjs().tz().isSame(date, 'day')) {
        return date.format('h:mm A');
    }

    const year = isCurrentYear(date) ? '' : ', YYYY';

    const DATE_PART_FORMAT = `${includeWeekday ? (shortWeekday ? 'ddd' : 'dddd') + separator : ''}MMM D${year}`;

    return date.format(DATE_PART_FORMAT) + separator + date.format('h:mm A');
};

export const shortDateDisplay = function (date: Dayjs) {
    if (!date) {
        return '';
    }

    if (dayjs().tz().isSame(dayjs(date).tz(), 'day')) {
        return date.format('h:mm A');
    }

    return formatWithAlternativeYear(date, 'MMM D', 'MMM D, YYYY');
};

export function shortDateTimeIntervalDisplay(start?: Dayjs, end?: Dayjs, separator = ' - ') {
    if (!start || !end) {
        return '';
    }

    const startDisplay = start.format('MM/DD/YY h:mm A');
    let endDisplay: string;

    if (start.isSame(end, 'day')) {
        endDisplay = end.format('h:mm A');
    } else {
        endDisplay = end.format('MM/DD/YY h:mm A');
    }

    return startDisplay + separator + endDisplay;
}

const formatDateOrTime = (date: Dayjs, type: 'date' | 'time') =>
    type === 'date' ? (isCurrentYear(date) ? 'MMM D' : 'MMM D, YYYY') : 'h:mm:ss A';

export function longIntervalDisplay(start?: Dayjs, end?: Dayjs, type: 'date' | 'time' = 'date', separator = ' – ') {
    if (!start || !end) {
        return '';
    }

    const startDisplay = start.format(formatDateOrTime(start, type));
    let endDisplay = '';

    if (type === 'time' || !start.isSame(end, 'day')) {
        endDisplay = end.format(formatDateOrTime(end, type));
    }

    if (!endDisplay) {
        return startDisplay;
    }

    return `${startDisplay}${separator}${endDisplay}`;
}

export const humanizeTime = function (date: Dayjs | string) {
    const parsed = typeof date === 'string' ? parseDate(date) : date;
    return parsed.fromNow();
};

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('time-ts', () => {
        it.todo('🤷🏼');
    });
}

// #endregion
